import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { ToastContainer, Bounce } from 'react-toastify';
import { useUser } from './UserContext';
import { AnnualPlanItem, LeaveRequest, User } from '../utils/types';
import { fetchData, hasRoleAccess, Notify } from '../utils/constants';
import { isWithinInterval, parseISO } from 'date-fns';
import LeaveReqModal from '../components/Modals/LeaveReqModal';

type NotificationsContextType = {
  nrDaysBeforeNotif: number;
  setNrDaysBeforeNotif: React.Dispatch<React.SetStateAction<number>>;
  // Own requests where the employee still needs to act (upload a proof
  // document, or re-upload one that was rejected) - drives the "My Requests"
  // sidebar badge so the task is visible without opening the page.
  documentsToUploadCount: number;
  refreshLeaveRequests: () => void;
};

type RequestDates = {
  startDate: Date;
  endDate: Date;
};

type RawAnnualPlanItem = Omit<AnnualPlanItem, 'startDate' | 'endDate'> & {
  startDate: string;
  endDate: string;
};

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { authUser, roles } = useUser();
  const [annualPlan, setAnnualPlan] = useState<AnnualPlanItem[]>([]);
  const [nrDaysBeforeNotif, setNrDaysBeforeNotif] = useState<number>(10);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [selfUser, setSelfUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [newRequestDates, setNewRequestDates] = useState<RequestDates>({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isEmployeeOnly = !hasRoleAccess(roles, ['HR', 'MANAGER', 'ADMIN']);
  const notifiedPlansRef = useRef<Set<number>>(new Set());

  const fetchAnnualPlan = useCallback(async () => {
    try {
      let rawData: RawAnnualPlanItem[] = [];

      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-planning/getLeaveSchedules`,
        (data: RawAnnualPlanItem[]) => {
          rawData = data;
        },
      );

      const normalized = rawData.map((item) => ({
        ...item,
        startDate: new Date(item.startDate),
        endDate: new Date(item.endDate),
      }));

      setAnnualPlan(normalized);
    } catch (err) {
      console.error('Annual plan fetch error:', err);
      Notify('error', 'Could not load annual plan data.');
    }
  }, []);

  const fetchMyLeaveReq = useCallback(async () => {
    const employeeId = selfUser?.id;
    if (typeof employeeId !== 'number') return;
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/getRequestsByEmployeeId/${employeeId}`,
        setLeaveRequests,
      );
    } catch (error) {
      console.error('Failed to fetch leave requests data:', error);
      Notify('error', 'Could not load leave requests data. Please try again.');
    }
  }, [selfUser]);

  const documentsToUploadCount = useMemo(
    () =>
      leaveRequests.filter(
        (lq) =>
          !!lq.documentDeadline || lq.documents?.[0]?.reviewStatus === 'REJECTED',
      ).length,
    [leaveRequests],
  );

  const fetchUsers = useCallback(async () => {
    const api = isEmployeeOnly
      ? `${process.env.REACT_APP_API_URL}/users/getAllUsers`
      : `${process.env.REACT_APP_API_URL}/users/displayAllUsers`;

    try {
      await fetchData(api, setUsers);
    } catch (error) {
      console.error('Failed to fetch users data:', error);
      Notify('error', 'Could not load users data. Please try again.');
    }
  }, [isEmployeeOnly]);

  const openLeaveRequestModal = (startDate: Date, endDate: Date) => {
    setNewRequestDates({ startDate, endDate });
    setIsOpen(true);
  };

  const notifyForLeaveRequest = useCallback(() => {
    const today = new Date();

    annualPlan.forEach((plan) => {
      if (notifiedPlansRef.current.has(plan.id)) return;

      const startDate = plan.startDate;
      const endDate = plan.endDate;

      const notifStartDate = new Date(startDate);
      notifStartDate.setDate(notifStartDate.getDate() - nrDaysBeforeNotif);

      const isInNotificationWindow = isWithinInterval(today, {
        start: notifStartDate,
        end: startDate,
      });

      if (!isInNotificationWindow) return;

      const overlappingLeaveRequestExists = leaveRequests.some((lr) => {
        if (
          lr.requestStatus !== 'APPROVED' &&
          lr.requestStatus !== 'AWAITING_MANAGER'
        )
          return false;

        const lrStart = parseISO(lr.startDate);
        const lrEnd = parseISO(lr.endDate);

        return lrStart <= endDate && lrEnd >= startDate;
      });

      if (overlappingLeaveRequestExists) return;

      Notify(
        'info',
        `Upcoming annual plan from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()} is starting soon! Click me to create new leave request!`,
        () => openLeaveRequestModal(startDate, endDate),
        `annual-plan-${plan.id}`,
      );

      notifiedPlansRef.current.add(plan.id);
    });
  }, [annualPlan, leaveRequests, nrDaysBeforeNotif]);

  useEffect(() => {
    if (!authUser) return;
    fetchAnnualPlan();
    fetchUsers();
  }, [authUser, fetchAnnualPlan, fetchUsers]);

  useEffect(() => {
    if (!annualPlan || annualPlan.length === 0) return;
    notifyForLeaveRequest();
  }, [annualPlan, notifyForLeaveRequest]);

  useEffect(() => {
    if (!selfUser) return;
    fetchMyLeaveReq();
  }, [fetchMyLeaveReq, selfUser]);

  useEffect(() => {
    if (authUser && users.length && !selfUser) {
      const self = users.find((u) => u.email === authUser.email);
      if (self) {
        setSelfUser(self);
      }
    }
  }, [authUser, selfUser, users]);

  return (
    <NotificationsContext.Provider
      value={{
        nrDaysBeforeNotif,
        setNrDaysBeforeNotif,
        documentsToUploadCount,
        refreshLeaveRequests: fetchMyLeaveReq,
      }}
    >
      {children}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />
      <LeaveReqModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectedStartDate={newRequestDates.startDate}
        selectedEndDate={newRequestDates.endDate}
        withButton={false}
      />
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context)
    throw new Error(
      'useNotifications must be used within a NotificationsProvider',
    );
  return context;
};
